import {AxiosInstance} from "axios";
import axios, {PREVIEW_URL} from "./axios";
import firebase from "config/firebase";
import {Course, IUser} from "./types";

class AccountantApi {
  api: AxiosInstance;
  constructor() {
    this.api = axios;

    this.api.interceptors.request.use(async (config) => {
      const bearer = await firebase.auth.currentUser?.getIdToken();
      config.headers.setAuthorization(`Bearer ${bearer}`);

      return config;
    });

    this.api.interceptors.response.use((res) => {
      return res;
    });
  }

  async signup(data: {
    bio: string;
    location: string;
    name: string;
    password: string;
    employmentType: string;
    softwareExperience: Record<string, boolean>;
    certifications: Record<string, string>;
    image?: string;
  }) {
    return this.api.post("/user/accountant/setup", data).then((res) => ({...res.data, justSignedUp: true}) as IUser);
  }

  async setVisibility(visible: boolean) {
    return this.api.patch("/user/accountant/visibility", {visible});
  }

  async updateProfile(data: {
    bio: string;
    state: string;
    city: string;
    name: string;
    zip: string;
    employmentType: string;
    softwareExperience: Record<string, boolean>;
    linkedIn?: string;
    alignable?: string;
  }) {
    return this.api.patch("/user/accountant/profile", data);
  }

  async updateCertifications(data: {updatedOld: Record<string, string>; newCertifications: Record<string, string>}) {
    return this.api
      .patch("/user/accountant/profile/certifications", data)
      .then((res) => res.data as Record<string, string>);
  }

  async updateCertification(name: string, certification: string | {url: string; imageUrl?: string} | null) {
    return this.api
      .patch("/user/accountant/profile/certification", {name, certification})
      .then((res) => res.data as Record<string, string | {url: string; imageUrl?: string}>);
  }

  async updateReviews(data: {reviews: Record<string, string | {url: string}>}) {
    return this.api
      .patch("/user/accountant/profile/reviews", {reviews: data.reviews})
      .then((res) => res.data as Record<string, string | {url: string}>);
  }

  async getCandidateListCount() {
    return this.api.get("/candidates/count").then((res) => res.data.count as number);
  }

  async deleteCandidate(id: string) {
    return this.api.delete(`/candidates/${id}`);
  }

  async openGym(magic: string) {
    return this.api.post("/user/accountant/gym", {magic}).then(
      (res) =>
        res.data as {
          token: string;
          courses: Record<string, Course>;
        },
    );
  }

  async shareProfile() {
    return this.api.post("/user/accountant/share").then((res) => {
      const token = res.data.token as string;
      const shareUrl = `${PREVIEW_URL}/sh/${token}`;
      return {token, shareUrl};
    });
  }

  async loadCertificationData(url: string) {
    return this.api
      .post("/config/certifications/extract-data", {url})
      .then((res) => res.data as {name?: string; imageUrl?: string});
  }

  async getCasMe() {
    return this.api.get("/cas/me").then(
      (res) =>
        res.data as {
          uid: string;
          name: string;
          tier: "free" | "education" | "course_access" | "pro";
          isDemo?: boolean;
        },
    );
  }

  async getBookWidgetsScores() {
    return this.api.get("/cas/bookwidgets-scores").then(
      (res) =>
        res.data as Record<
          string,
          {
            score: number | null;
            avgScore: number | null;
            widgetCount: number;
            completedWidgetCount: number;
            widgets: {
              widgetId: number;
              widgetName: string | null;
              widgetType: string | null;
              bestScore: number | null;
              playUrl: string | null;
            }[];
          }
        >,
    );
  }

  async getUserFoundationalKnowledge(userId: string) {
    return this.api.get(`/user/${userId}/foundational-knowledge`).then(
      (res) =>
        res.data as {
          pathways: Awaited<ReturnType<AccountantApi["getCasPathways"]>>;
          scores: Awaited<ReturnType<AccountantApi["getBookWidgetsScores"]>>;
          addOnPreferences: Record<string, boolean>;
          courseGroups: Awaited<ReturnType<AccountantApi["getCourseGroups"]>>;
        },
    );
  }

  async getCasPathways() {
    return this.api.get("/cas/pathways").then(
      (res) =>
        res.data as {
          pathwayId: number;
          pathwayName: string;
          pathwayType: string;
          pathwayOrder: number;
          courses: {
            pathwayCourseId: number;
            courseId: number;
            courseTitle: string;
            courseSlug: string | null;
            courseDescription: string | null;
            courseOrder: number;
            addOnGroup: string | null;
            groupLabel: string | null;
            sectionLabel: string | null;
            courseGroupDisplayMode: string | null;
            isComplete: boolean | null;
            isPurchased: boolean;
            lessonCount: number;
            completedLessonCount: number;
            score: null;
            modules: {
              moduleId: number;
              modulePosition: number;
              moduleLabel: string;
              moduleSlug: string | null;
              moduleDescription: string | null;
              durationMinutes: number | null;
              lessonCount: number;
              completedLessonCount: number;
              isComplete: boolean | null;
            }[];
          }[];
          milestones: {
            milestoneId: number;
            tagName: string;
            displayOrder: number;
            certLabel: string | null;
            certImageUrl: string | null;
            requirementsText: string | null;
            subtitle: string | null;
            isEarned: boolean;
            afterPathwayCourseId: number | null;
          }[];
        }[],
    );
  }

  async getCasProfile() {
    return this.api.get("/cas/profile").then(
      (res) =>
        res.data as {
          uid: string;
          name: string;
          tier: string;
          published: boolean;
          stageProgress: {
            id: number;
            label: string;
            status: "complete" | "active" | "locked";
            modules: number;
            completedModules: number;
          }[];
          earnedBadgeTiers: string[];
          purchasedStageIds: number[];
          addOnPreferences: Record<string, boolean>;
          hasBooksMatchAccess: boolean;
        },
    );
  }

  async getCourseGroups() {
    return this.api.get("/cas/course-groups").then(
      (res) =>
        res.data as {
          groupKey: string;
          label: string;
          customizeText: string | null;
          displayOrder: number;
          displayMode: string;
          visibilityEditable: boolean;
          defaultEnabled: boolean;
        }[],
    );
  }

  async getCalendarEvents(from: number, to: number) {
    return this.api.get(`/cas/calendar?from=${from}&to=${to}`).then(
      (res) =>
        res.data as {
          occurrenceId: number;
          eventTypeId: number;
          typeName: string;
          typeColor: string | null;
          title: string;
          startUtc: string;
          endUtc: string;
          meetingLink: string | null;
          locked: boolean;
        }[],
    );
  }
}

const accountantApi = new AccountantApi();

export default accountantApi;
